'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';

export default function MouseStalker() {
    const [hasPointer, setHasPointer] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isClicked, setIsClicked] = useState(false);
    const positionRef = useRef({ x: 0, y: 0 });
    const rafRef = useRef<number | null>(null);
    const dirtyRef = useRef(false);
    const isMonitorOpen = useBoardEditorStore((s) => s.isMonitorOpen);

    useEffect(() => {
        const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
        setHasPointer(hasFinePointer);
        if (!hasFinePointer) return;

        const handleMouseMove = (event: MouseEvent) => {
            positionRef.current = { x: event.clientX, y: event.clientY };
            dirtyRef.current = true;
        };

        const tick = () => {
            if (dirtyRef.current) {
                setPosition({ x: positionRef.current.x, y: positionRef.current.y });
                dirtyRef.current = false;
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        const handleMouseDown = () => setIsClicked(true);
        const handleMouseUp = () => setIsClicked(false);

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, []);

    if (!hasPointer) return null;
    if (isMonitorOpen) return null;

    return (
        <motion.div
            className="fixed top-0 left-0 w-12 h-12 pointer-events-none z-[100]"
            animate={{
                x: position.x + 20,
                y: position.y + 20,
                scale: isClicked ? 1.5 : 1,
            }}
            transition={{ type: 'spring', stiffness: 150, damping: 20 }}
        >
            <Image src="/images/character/schoomy.png" alt="" width={22} height={22} unoptimized />
        </motion.div>
    );
}
